/**
 * 백엔드가 저장한 한글 시스템 메시지(채팅)·알림을 영어로 변환 (i18n 3단계)
 * DB에는 한글 원문이 저장되므로, 영어 모드일 때 패턴 매칭으로 번역해 표시.
 * 매칭 실패 시 원문 그대로 반환 (안전 fallback).
 */
import i18n from './index';

const currentLang = (): string => (i18n as unknown as { language?: string }).language || 'en';

type Rule = [RegExp, (m: RegExpMatchArray) => string];

const RULES: Rule[] = [
  [/^📋 (.+?)님이 거래를 시작했습니다\. \(([\s\S]+)\)$/, m => `📋 ${m[1]} started a trade. (${m[2]})`],
  [/^✅ (.+?)님이 거래를 수락했습니다\. 거래가 진행 중입니다\.$/, m => `✅ ${m[1]} accepted the trade. It is now in progress.`],
  [/^📝 (.+?)님이 거래 완료를 확인했습니다\. 상대방의 확인을 기다리는 중입니다\.$/, m => `📝 ${m[1]} confirmed completion. Waiting for the other party.`],
  [/^❌ (.+?)님이 거래를 취소했습니다\.(?: 사유: ([\s\S]*))?$/, m => m[2] ? `❌ ${m[1]} cancelled the trade. Reason: ${m[2]}` : `❌ ${m[1]} cancelled the trade.`],
  [/^거래가 완료되었습니다! 리뷰를 남겨주세요\.$/, () => 'The trade is complete! Please leave a review.'],
  [/^거래가 완료되었습니다\. 리뷰를 남겨주세요!$/, () => 'The trade is complete. Please leave a review!'],
  [/^전문가가 작업 완료를 알렸습니다\. 의뢰인의 확인이 필요합니다\.$/, () => 'The expert reported the work as done. The client needs to confirm.'],
  [/^✅ 프로젝트가 완료되었습니다! 정산이 진행됩니다\.$/, () => '✅ The project is complete! Settlement is in progress.'],
  [/^(의뢰자|전문가)가 계약에 동의했습니다\. 상대방의 동의를 기다리는 중입니다\.$/, m => `The ${m[1] === '의뢰자' ? 'client' : 'expert'} agreed to the contract. Waiting for the other party.`],
  [/^✅ 견적이 수락되었습니다\. 결제를 진행해주세요\.$/, () => '✅ The quote was accepted. Please proceed with payment.'],
  [/^❌ 견적이 거절되었습니다\.(?: 사유: ([\s\S]*))?$/, m => m[1] ? `❌ The quote was rejected. Reason: ${m[1]}` : '❌ The quote was rejected.'],
  [/^✅ 제안서가 수락되었습니다\. 계약 세부사항을 협의해주세요\.$/, () => '✅ The proposal was accepted. Please discuss the contract details.'],
  [/^❌ 제안서가 거절되었습니다\.(?: 사유: ([\s\S]*))?$/, m => m[1] ? `❌ The proposal was rejected. Reason: ${m[1]}` : '❌ The proposal was rejected.'],
  [/^🎉 양측이 계약에 동의했습니다! 프로젝트가 시작되었습니다\.$/, () => '🎉 Both parties agreed! The project has started.'],
  [/^📄 (.+?)님이 AI 계약서 초안을 생성했습니다\.$/, m => `📄 ${m[1]} generated an AI contract draft.`],
];

/** 채팅 시스템 메시지 번역 (영어 모드에서만 변환) */
export const localizeSystemMessage = (text?: string | null): string => {
  if (!text) return '';
  if (currentLang().startsWith('ko')) return text;
  for (const [re, fn] of RULES) {
    const m = text.match(re);
    if (m) return fn(m);
  }
  return text;
};

/** 인앱 알림(title/body) 번역 — type 기반 + 파라미터 추출 */
export const localizeNotification = (n: { type: string; title: string; body: string | null }): { title: string; body: string | null } => {
  if (currentLang().startsWith('ko')) return { title: n.title, body: n.body };
  if (n.type === 'trade_complete') {
    return {
      title: 'Trade completed',
      body: n.body ? 'The trade was completed successfully. Leave a review for the other party.' : n.body,
    };
  }
  if (n.type === 'review_received') {
    const m = (n.body || '').match(/^(.+?)님이 리뷰를 남겼습니다: ([\s\S]*)$/);
    return {
      title: 'New review received',
      body: m ? `${m[1]} left a review: ${m[2]}` : n.body,
    };
  }
  return { title: n.title, body: n.body };
};
